import React, { PropsWithChildren, useContext, useEffect, useState, FC } from 'react';
import {
	Accordion as BootstrapAccordion,
	AccordionContext,
	AccordionProps as BootstrapAccordionProps,
	AccordionCollapseProps as BoostrapAccordionCollapseProps,
	useAccordionToggle,
} from 'react-bootstrap';

import { findChild } from '../../utils/reactUtils';
import FAIcon from '../FAIcon/FAIcon';

import './Accordion.css';

interface AccordionSubcomponents {
	Item: typeof Item;
	Heading: typeof Heading;
	Collapse: typeof Collapse;
}

type AccordionProps = PropsWithChildren<Omit<BootstrapAccordionProps, 'activeKey'>>;

const Accordion: FC<AccordionProps> & AccordionSubcomponents = ({
	defaultActiveKey,
	children,
	onSelect,
}) => {
	const [activeKey, setActiveKey] = useState<string | undefined>(defaultActiveKey);
	useEffect(() => {
		// reset current activeKey if defaultActiveKey ever changes
		if (activeKey !== defaultActiveKey) {
			setActiveKey(defaultActiveKey);
		}
	}, [defaultActiveKey]);

	return (
		<BootstrapAccordion
            className="Accordion"
			onSelect={(eventKey, event) => {
				onSelect && onSelect(eventKey, event);
				setActiveKey(eventKey ?? undefined);
			}}
			activeKey={activeKey}
		>
			{children}
		</BootstrapAccordion>
	);
};

const Item: FC<PropsWithChildren<{ eventKey: string }>> = ({ children, eventKey }) => {
	const headingNode = children && findChild(children, Heading);
	const otherNodes =
		(children || null) && (
			<>
				{React.Children.toArray(children).filter(
					(child: any) => typeof child !== 'object' ||
						('type' in child &&
						![Heading].includes(child.type)),
				)}
			</>
		);

	return (
		<div className="Accordion__item mb-3">
			<Toggle eventKey={eventKey}>{headingNode}</Toggle>
			<BootstrapAccordion.Collapse eventKey={eventKey}>
				<>{otherNodes}</>
			</BootstrapAccordion.Collapse>
		</div>
	);
};

const Heading: FC<PropsWithChildren<{}>> = ({ children }) => <>{children}</>;

const Toggle: FC<PropsWithChildren<{ eventKey: string }>> = ({ children, eventKey }) => {
	const currentEventKey = useContext(AccordionContext);
	const decoratedOnClick = useAccordionToggle(eventKey);
	const isCurrentEventKey = currentEventKey === eventKey;

	return (
		<h4 className="Accordion__toggle mb-3 cursor-pointer" onClick={decoratedOnClick}>
			{isCurrentEventKey ? (
				<FAIcon className="mr-2" name="chevron-up" />
			) : (
				<FAIcon className="mr-2" name="chevron-down" />
			)}
			{children}
		</h4>
	);
};

const Collapse: FC<PropsWithChildren<BoostrapAccordionCollapseProps>> = ({
	children,
	eventKey,
}) => {
	return (
		<BootstrapAccordion.Collapse as={'span'} eventKey={eventKey}>
			{children}
		</BootstrapAccordion.Collapse>
	);
};

Accordion.Item = Item;
Accordion.Collapse = Collapse;
Accordion.Heading = Heading;

export default Accordion;
